import { z } from 'zod';
import { addDays, isDueOn } from './schedule';
import type { Repeat, TaskInstanceStatus } from './schemas';

/**
 * Streaks ("🔥 5 Tage am Stück"): how many due days in a row one person did one task.
 * Only days the task is due count – a weekday task skips the weekend, a Mo/Mi task skips Di.
 * Today does not break a streak while it is still open; it counts once ticked.
 */

/** Waiting for the parents' OK counts – the kid did it; "Nochmal" reopens and recounts. */
export const countsForStreak = (s: TaskInstanceStatus | undefined) =>
  s === 'done' || s === 'pending' || s === 'approved';

/** How far back the server looks. Longer streaks show as "60+". */
export const STREAK_WINDOW = 60;

export const Streak = z.object({
  memberId: z.string(),
  taskId: z.string(),
  count: z.number().int().min(0),
  /** Last day of the streak (YYYY-MM-DD). */
  lastDone: z.string().optional(),
  best: z.number().int().min(0),
  updatedAt: z.number().int(),
});
export type Streak = z.infer<typeof Streak>;

/** `done(day)` says whether the person did the task that day. */
export function streakLength(
  repeat: Repeat,
  done: (day: string) => boolean,
  today: string,
  window = STREAK_WINDOW,
): { count: number; lastDone?: string } {
  if (repeat.kind === 'once') return { count: 0 };
  let count = 0;
  let lastDone: string | undefined;
  for (let i = 0; i < window; i++) {
    const day = addDays(today, -i);
    if (!isDueOn(repeat, day)) continue;
    if (done(day)) {
      count++;
      lastDone ??= day;
    } else if (i > 0) {
      break; // a missed due day before today ends the streak
    }
  }
  return { count, lastDone };
}

/**
 * The server writes a streak when somebody ticks – if nobody ticks, it is not rewritten. So the
 * app asks: was a due day missed between the last done day and today? Then it is over.
 */
export function streakAlive(repeat: Repeat, lastDone: string | undefined, today: string): boolean {
  if (!lastDone || lastDone > today) return Boolean(lastDone);
  for (let day = addDays(lastDone, 1); day < today; day = addDays(day, 1)) {
    if (isDueOn(repeat, day)) return false;
  }
  return true;
}

/** Shown from two days on – one day is not a streak yet. */
export const MIN_STREAK = 2;
